import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../../context';
import { ChannelAppTabAction, StoryPinned } from '../../../../../services';
import { FilterValue, optArrItem, useAppTabsLabel, useFilter } from '../../../../../shared';

export enum DisplayProps {
  includeInWebappNewsFeed = 'includeInWebappNewsFeed',
  includeInNewsFeed = 'includeInNewsFeed',
  includeInMixTab = 'includeInMixTab',
  includeInMixTab2 = 'includeInMixTab2',
  includeInMixTab3 = 'includeInMixTab3',
  includeInMixTab4 = 'includeInMixTab4',
  includeInMixTab5 = 'includeInMixTab5',
  pinned = 'pinned',
  pinned2 = 'pinned2',
  pinned3 = 'pinned3',
  pinned4 = 'pinned4',
  pinned5 = 'pinned5',
  horizontalPosition = 'position',
}

export const useCreateStoryDisplayFilter = (filter: FilterValue[]) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'createStorySettings' });

  const { getAppTabsLabel } = useAppTabsLabel();

  const { getGroupItemValue } = useFilter();

  const { userPermissions } = useContext(UserContext).userInfo.userData;

  const { allowToPinStoryToOrganisation } = userPermissions;

  const pinnedValue = useMemo(
    () =>
      getGroupItemValue({ filter, groupId: 'display', itemId: DisplayProps.pinned }) ??
      StoryPinned.CHANNEL,
    [filter, getGroupItemValue]
  );

  const horizontalPosition = useMemo(
    () =>
      getGroupItemValue({ filter, groupId: 'display', itemId: DisplayProps.horizontalPosition }) ??
      1,
    [filter, getGroupItemValue]
  );

  return {
    groups: [
      {
        id: 'display',
        multiCheck: true,
        items: [
          {
            id: DisplayProps.includeInWebappNewsFeed,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t(
              'common.web'
            )}`,
            value: DisplayProps.includeInWebappNewsFeed,
          },
          {
            id: DisplayProps.includeInNewsFeed,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t(
              'common.mobile'
            )}`,
            value: DisplayProps.includeInNewsFeed,
          },
          {
            id: DisplayProps.includeInMixTab,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.STORIES)}`,
            value: DisplayProps.includeInMixTab,
          },
          {
            id: DisplayProps.includeInMixTab2,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.STORIES_2)}`,
            value: DisplayProps.includeInMixTab2,
          },
          {
            id: DisplayProps.includeInMixTab3,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.STORIES_3)}`,
            value: DisplayProps.includeInMixTab3,
          },
          {
            id: DisplayProps.includeInMixTab4,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.STORIES_4)}`,
            value: DisplayProps.includeInMixTab4,
          },
          {
            id: DisplayProps.includeInMixTab5,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.STORIES_5)}`,
            value: DisplayProps.includeInMixTab5,
          },
          {
            id: DisplayProps.pinned,
            label: `${t('common.display-in')} ${getAppTabsLabel(ChannelAppTabAction.PINNED_STORY)}`,
            value: pinnedValue,
            dropdown: {
              options: [
                {
                  id: StoryPinned.CHANNEL,
                  title: ctxT('display-in-pinned-mix-channel'),
                },
                ...optArrItem(allowToPinStoryToOrganisation, {
                  id: StoryPinned.ORGANISATION,
                  title: ctxT('display-in-pinned-mix-organisation'),
                }),
              ],
            },
          },
          {
            id: DisplayProps.pinned2,
            label: `${t('common.display-in')} ${getAppTabsLabel(
              ChannelAppTabAction.PINNED_STORY_2
            )}`,
            value: StoryPinned.CHANNEL,
          },
          {
            id: DisplayProps.pinned3,
            label: `${t('common.display-in')} ${getAppTabsLabel(
              ChannelAppTabAction.PINNED_STORY_3
            )}`,
            value: StoryPinned.CHANNEL,
          },
          {
            id: DisplayProps.pinned4,
            label: `${t('common.display-in')} ${getAppTabsLabel(
              ChannelAppTabAction.PINNED_STORY_4
            )}`,
            value: StoryPinned.CHANNEL,
          },
          {
            id: DisplayProps.pinned5,
            label: `${t('common.display-in')} ${getAppTabsLabel(
              ChannelAppTabAction.PINNED_STORY_5
            )}`,
            value: StoryPinned.CHANNEL,
          },
          {
            id: DisplayProps.horizontalPosition,
            label: ctxT('display-in-horizontal-mix'),
            value: horizontalPosition,
            inputNumber: {
              label: `${t('common.position-in')} ${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)}`,
              min: 1,
              max: 100,
            },
          },
        ],
      },
    ],
  };
};
